import {
    AlertSubTypesEnum,
    AlertTypesEnum,
    DataScopesEnum,
    EmptyGuid,
    EntityTypes,
    LookupDataEnums,
    StrategyTypes,
    SystemGeneratedDocumentsTypesEnum,
} from '../utilities/Constants';
import {
    IAlertListWithCountModel,
    IAlertSearchModel,
    IAlertSummaryModel,
    INoteCategoryTypeModel,
    INoteModel,
} from '../interfaces/INote';
import { IApiRequest, IApiResponse, ApiClient as TpiApiClient } from '../tpi.apiclient';
import {
    IAssertionDescriptorModel,
    IEntityAssertionModel,
} from '../interfaces/IAssertionDescriptor';
import {
    IAssignmentModel,
    ICaseAuditLog,
    ICaseDeadlineModel,
    ICaseModel,
    ICasePlanTemplate,
    ICaseSearchModel,
    ICaseTitleModel,
    ICheckmateTaskModel,
    ICreateCheckmateTaskModel,
    IDepositionModel,
    IDocumentDeleteRequestModel,
    IDocumentModel,
    IDocumentRequestModel,
    IPackageSettlementBaseModel,
    IPackageSettlementModel,
} from '../interfaces/ICase';
import {
    IAssociatedOrganizationModel,
    IOrganizationModel,
    IOrganizationSettingModel,
} from '../interfaces/IOrganization';
import {
    IBestFitCase,
    ILikeCaseStatistics,
    IPredictedOutcomeOutput,
    IThreatScore,
} from '../interfaces/IAssessment';
import { IBoardComment, IBoardParametersModel } from '../interfaces/Report/IBoard';
import {
    ICaseExpertModel,
    ICaseExpertViewDetailModel,
    ICreateCaseExpertModel,
    IUpdateCaseExpertModel,
} from '../interfaces/ICaseExpert';
import {
    ICreateStrategyModel,
    IJurisdictionStrategyDetailModel,
    ILocalCounselStrategyDetailModel,
    IPlaintiffsFirmStrategyDetailModel,
    IProjectModel,
    IStrategySummaryModel,
    IUpdateStrategyModel,
} from '../interfaces/IStrategy';
import { IDocumentSearchModel, IViewDocumentModel } from '../interfaces/IDocument';
import {
    IEntityConversationCreateModel,
    IEntityConversationModel,
    IReportModel,
    IReportSaveModel,
    IReportTypeModel,
    IUpdateAlertUserModel,
    IUserAuditModel,
} from '../interfaces/Report/IReport';
import {
    IExpertCreateModel,
    IExpertDetailModel,
    IExpertListModel,
    IExpertModel,
} from '../interfaces/IExpert';
import { ILookupModel, IStaticItemLookupModel } from '../interfaces/ILookup';
import { INoteUISearchModel, IQueryResultModel, IQuerySettingsModel } from '../interfaces/IQuery';
import { IResponse, IResponseModel } from '../interfaces/IResponse';
import { IUserModel, IUserZoneProfileSearchModel } from '../interfaces/IUser';

import { ApiRoutes } from '../utilities/ApiRoutes';
import { ICollabReportStatusModel } from '../interfaces/ICollabReport';
import { IDisplaySectionModel } from '../interfaces/IDisplaySectionModel';
import { IFavoriteModel } from '../interfaces/IFavorite';
import { IObtainEntityLeaseResponse } from '../interfaces/IEntityLease';
import { IPackageSettlementSearchModel } from '../interfaces/IPackageSettlement';
import { IReportCaseDetailModel } from '../interfaces/Report/IReportCaseDetailModel';
import { ISaveDocumentMetaData as ISaveDocumentMetaDataRequest } from '../components/shared/document-upload/useSaveDocumentMetaDataMutation';
import { ISnapshotViewModel } from '../interfaces/ISnapshotView';
import { ITimelineReportInputModel } from '../interfaces/Report/ITimelineReport';
import { ITriggerRuleSettingsModel } from '../interfaces/ITriggerRule';
import { IUserZoneProfileModel } from '../interfaces/ITask';
import { config } from '../config/config';

class CheckmateZoneApiClient extends TpiApiClient {
    constructor(guid: string) {
        super(config.baseUrl, guid);
    }

    /**
     * Prepares the request for transmission.
     * @param {ApiRequest} request The request object.
     * @returns {Promise<ApiRequest>} The prepared request object.
     */
    async prepareRequest<T>(request: IApiRequest<T>): Promise<IApiRequest<T>> {
        request = await super.prepareRequest(request);

        request.httpRequest.headers.set('User-Agent', 'CheckmateApiClient/1.0');
        request.httpRequest.headers.set('Credentials', 'same-origin');

        return request;
    }

    /**
     * Processes a response after completion.
     * @param {ApiRequest} request The request object.
     * @param {ApiResponse} response The response object.
     * @returns {Promise<ApiResponse>} The processed response object.
     */
    async processResponse<T>(
        request: IApiRequest<any>,
        response: IApiResponse<T>
    ): Promise<IApiResponse<T>> {
        response = await super.processResponse(request, response);
        return response;
    }

    // CASES
    getCases(caseFilter: ICaseSearchModel) {
        const body: ICaseSearchModel = caseFilter;
        return this.post<ICaseModel[]>(ApiRoutes.Cases, { body });
    }

    getCaseById(guid: string, queryString?: string) {
        let url = ApiRoutes.CaseDetails.replace('{id}', guid);
        if (queryString) url += queryString;
        return this.get<ICaseModel>(url);
    }

    getCaseFileModel() {
        return this.get<IDisplaySectionModel[]>(ApiRoutes.CaseFileModel);
    }

    getCaseQueryResults<T extends IQueryResultModel | ICaseExpertModel[]>(
        querySettings: IQuerySettingsModel
    ) {
        return this.post<T>(ApiRoutes.Query, { body: querySettings });
    }

    getCaseAuditLogs(guid: string) {
        let url = ApiRoutes.CaseAuditLogs.replace('{id}', guid);
        return this.get<ICaseAuditLog[]>(url);
    }

    getCaseQueryCount(querySettings: IQuerySettingsModel) {
        const body: IQuerySettingsModel = querySettings;
        return this.post<IResponseModel<number>>(ApiRoutes.Query + '?count=true', {
            body,
        });
    }

    getCaseCount(caseFilter: ICaseSearchModel) {
        const body: ICaseSearchModel = caseFilter;
        return this.post<IResponseModel<number>>(ApiRoutes.Cases + '?count=true', {
            body,
        });
    }

    updateCase(caseProfile: ICaseModel) {
        return this.put<ICaseModel>(ApiRoutes.Case, { body: caseProfile });
    }

    updateCasePriority(caseGuid: string, priority: string) {
        return this.put<ICaseTitleModel>(
            ApiRoutes.CasePriority.replace('{id}', caseGuid).replace('{priority}', priority)
        );
    }

    createCase(caseProfile: ICaseModel) {
        return this.post<ICaseModel>(ApiRoutes.Case, { body: caseProfile });
    }

    deleteCase(caseGuid: string) {
        return this.delete(ApiRoutes.CaseDetails.replace('{id}', caseGuid));
    }

    easyUpdateCase(caseProfile: ICaseModel) {
        return this.patch<ICaseModel>(ApiRoutes.Case, { body: caseProfile });
    }

    updateCaseDeadlines(caseGuid: string, coreDeadlines: ICaseDeadlineModel[]) {
        const body: ICaseDeadlineModel[] = coreDeadlines;
        return this.put<IUserModel>(ApiRoutes.CaseDeadlines.replace('{id}', caseGuid), { body });
    }

    createCasePlan(caseGuid: string, templateGuid: string, levelOfEffortId: number) {
        return this.post<ICasePlanTemplate[]>(
            ApiRoutes.CasePlan.replace('{id}', caseGuid)
                .replace('{templateid}', templateGuid)
                .replace('{loeid}', levelOfEffortId.toString())
        );
    }

    // NOTES
    getNoteDetails(id: string) {
        const url = ApiRoutes.NoteDetail.replace('{id}', id);
        return this.get<INoteModel>(url);
    }

    saveNote(note: INoteModel) {
        const body: INoteModel = note;
        return note.guid
            ? this.put<INoteModel>(ApiRoutes.Note, { body })
            : this.post<INoteModel>(ApiRoutes.Note, { body });
    }

    getNotes(typeId: number, key?: string) {
        let url = ApiRoutes.Notes + '?typeId=' + typeId.toString();
        if (key) url += '&key=' + key;
        return this.get<INoteModel[]>(url);
    }

    deleteNote(noteGuid: string, key?: string) {
        let route = ApiRoutes.NoteDetail.replace('{id}', noteGuid);
        if (key) route += '?key=' + key;
        return this.delete(route);
    }

    searchNotes(searchModel: INoteUISearchModel) {
        const body: INoteUISearchModel = searchModel;
        return this.post<INoteModel[]>(ApiRoutes.Notes, { body });
    }

    searchPackageDealNotes(querySettings: IQuerySettingsModel) {
        const body: IQuerySettingsModel = querySettings;
        return this.post<INoteModel[]>(ApiRoutes.DealNotes + '?count=true', {
            body,
        });
    }

    getCaseManagersInZone() {
        return this.getUsersInZone(DataScopesEnum.National.Value);
    }

    // USERS
    getLoggedInUserInfo() {
        return this.get<IUserModel>(ApiRoutes.LoggedInUserDetails);
    }

    getUsersInZone(dataScope?: number, filterUserRights?: number[]) {
        let url = ApiRoutes.UsersInZone;

        const queryStrings: string[] = [];
        if (dataScope) queryStrings.push('dataScope=' + dataScope.toString());
        if (filterUserRights) {
            const queryParams = filterUserRights.map((id) => `userRights=${id}`).join('&');
            queryStrings.push(queryParams);
        }
        if (queryStrings.length > 0) url += '?' + queryStrings.join('&');

        return this.get<IUserModel[]>(url);
    }

    createUserZoneProfile(userZoneProfile: IUserZoneProfileModel) {
        const body: IUserZoneProfileModel = userZoneProfile;
        return this.post<IUserZoneProfileModel>(ApiRoutes.UserZoneProfile, {
            body,
        });
    }

    updateUserZoneProfile(userZoneProfile: IUserZoneProfileModel) {
        const body: IUserZoneProfileModel = userZoneProfile;
        return this.put<IUserZoneProfileModel>(ApiRoutes.UserZoneProfile, { body });
    }

    // ORGANIZTIONS
    getCurrentUserOrganizationFromServer(organizationGuid: string) {
        return this.getOrganizationById(organizationGuid);
    }

    getOrganizationById(guid: string) {
        return this.get<IOrganizationModel>(ApiRoutes.OrganizationDetails.replace('{id}', guid));
    }

    getOrganizationsInZone(organizationTypeId?: number) {
        let route = ApiRoutes.OrganizationsInZone;
        if (organizationTypeId != null) {
            route += '?organizationTypeId=' + organizationTypeId;
        }

        return this.get<IAssociatedOrganizationModel[]>(route);
    }

    getOrganizationsByCaseFileModel(caseFileModelId: number) {
        return this.get<IOrganizationModel[]>(
            ApiRoutes.OrganizationsByCaseFileModel.replace(
                '{caseFileModelId}',
                caseFileModelId.toString()
            )
        );
    }

    getOrganizationSettings(settingName?: string) {
        let url = ApiRoutes.OrganizationSettings;
        if (settingName) url += '?settingName=' + settingName;
        return this.get<IOrganizationSettingModel[]>(url);
    }

    // NOTES
    getNoteCategoryTypesByEntityType(
        entityType?: EntityTypes
    ): Promise<IApiResponse<INoteCategoryTypeModel[]>> {
        let response = null;
        switch (entityType) {
            case EntityTypes.Expert:
            case EntityTypes.PackageSettlement:
            case EntityTypes.Deposition:
            case EntityTypes.Organization:
            case EntityTypes.Project:
            case EntityTypes.AssertionDescriptorValueStrategy:
                response = this.getNoteCategoryTypes(LookupDataEnums.StrategyNoteCategories);
                break;
            case EntityTypes.Case:
            case EntityTypes.CaseExpert:
                response = this.getNoteCategoryTypes(LookupDataEnums.CaseNoteCategories);
                break;
            default: {
                response = this.getNoteCategoryTypes();
            }
        }

        return response;
    }

    getNoteCategoryTypes(type?: LookupDataEnums) {
        if (type) {
            return this.get<INoteCategoryTypeModel[]>(ApiRoutes.Lookup + type);
        } else
            return this.get<INoteCategoryTypeModel[]>(
                ApiRoutes.Lookup + LookupDataEnums.NoteCategories
            );
    }

    getNoteTargets(notetypeId: number, caseLocalCounselGuid?: string, strategyTypeId?: number) {
        let url = ApiRoutes.NoteTargets.replace('{noteTypeId}', notetypeId.toString());

        const queryStringParams: string[] = [];
        if (caseLocalCounselGuid && caseLocalCounselGuid !== EmptyGuid)
            queryStringParams.push('localCounsel=' + caseLocalCounselGuid);
        if (strategyTypeId) queryStringParams.push('strategyTypeId=' + strategyTypeId);
        if (queryStringParams.length > 0) url += '?' + queryStringParams.join('&');
        return this.get<IUserModel[]>(url);
    }

    // ALERTS
    markAlertAsRead(id: string, alertType?: AlertTypesEnum, alertSubType?: AlertSubTypesEnum) {
        let url = ApiRoutes.MarkAlertAsReadForUser.replace('{id}', id);
        const queryStringParams: string[] = [];

        if (alertType) queryStringParams.push('alertTypes=' + alertType);
        if (alertSubType) queryStringParams.push('alertSubTypes=' + alertSubType);
        if (queryStringParams.length > 0) url += '?' + queryStringParams.join('&');

        return this.put<IResponse>(url);
    }

    closeAlert(model: IUpdateAlertUserModel) {
        return this.put<unknown>(ApiRoutes.CloseAct, { body: model });
    }

    searchAlerts(searchModel: IAlertSearchModel) {
        const body: IAlertSearchModel = searchModel;
        const url = ApiRoutes.Alerts;
        return this.post<IAlertListWithCountModel>(url, { body });
    }

    getAlertBarInfo() {
        return this.get<IAlertSummaryModel[]>(ApiRoutes.UserAlertSummary);
    }

    // LOOKUP
    getZoneSpecificLookupData(lookupType: LookupDataEnums) {
        return this.get<ILookupModel[]>(ApiRoutes.Lookup + lookupType);
    }

    getCaseFileStaticItems() {
        return this.get<IStaticItemLookupModel[]>(ApiRoutes.Lookup + '/CaseFileStaticItems');
    }

    //Assertion Descriptor Api Calls
    getAssertionDescriptors(
        usageTypeId: number,
        searchString?: string,
        checkForEditAccess?: boolean
    ) {
        const body: any = {
            usageTypeId,
            searchString,
            checkForEditAccess,
        };

        return this.post<IAssertionDescriptorModel[]>(ApiRoutes.AssertionDescriptors, {
            body,
        });
    }

    // query
    getCaseNotesQueryResults(querySettings: IQuerySettingsModel) {
        const body: IQuerySettingsModel = querySettings;
        return this.post<INoteModel[]>(ApiRoutes.Query, { body });
    }

    getDepositionQueryResults(querySettings: IQuerySettingsModel) {
        const body: IQuerySettingsModel = querySettings;
        return this.post<IDepositionModel[]>(ApiRoutes.Query, { body });
    }

    // PACKAGE SETTLEMENT
    getPackageSettlements(params?: IPackageSettlementSearchModel) {
        const baseUrl = ApiRoutes.PackageSettlements;
        const searchParams = new URLSearchParams();

        Object.entries(params ?? {}).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach((val) => {
                    searchParams.append(key, val.toString());
                });
            } else if (value !== undefined) {
                searchParams.set(key, value);
            }
        });

        const queryString = searchParams?.toString();
        const url = queryString ? `${baseUrl}?${queryString}` : baseUrl;

        return this.get<IPackageSettlementBaseModel[]>(url);
    }

    createPackageSettlement(packageSettlement: IPackageSettlementModel) {
        const body: IPackageSettlementModel = packageSettlement;
        return this.post<IPackageSettlementModel>(ApiRoutes.PackageSettlement, {
            body,
        });
    }

    updatePackageSettlement(packageSettlement: IPackageSettlementModel) {
        const body: IPackageSettlementModel = packageSettlement;
        return this.put<IPackageSettlementModel>(ApiRoutes.PackageSettlement, {
            body,
        });
    }

    deletePackageSettlement(guid: string) {
        return this.delete<IResponse>(ApiRoutes.PackageSettlementDetail.replace('{id}', guid));
    }

    getPackageSettlementDetail(guid: string) {
        return this.get<IPackageSettlementModel>(
            ApiRoutes.PackageSettlementDetail.replace('{id}', guid)
        );
    }

    runPackageSettlementSearch(caseFilter: ICaseSearchModel) {
        const body: ICaseSearchModel = caseFilter;
        return this.post<IReportCaseDetailModel[]>(ApiRoutes.Cases, { body });
    }

    getPackageSettlementDetailWithReport(guid: string, includeHistoricData: boolean) {
        return this.get<IPackageSettlementModel>(
            ApiRoutes.PackageSettlementDetail.replace('{id}', guid) +
                `?report=true&includeHistoricData=${includeHistoricData}`
        );
    }

    // Triggers
    createTrigger(trigger: ITriggerRuleSettingsModel) {
        return this.post<ITriggerRuleSettingsModel>(ApiRoutes.TriggerCreate, { body: trigger });
    }

    searchTriggers(params: any = {}) {
        return this.post<ITriggerRuleSettingsModel[]>(ApiRoutes.TriggerSearch, { data: params });
    }

    getTrigger(guid: string) {
        const uri = ApiRoutes.TriggerGet.replace('{guid}', guid);
        return this.get<ITriggerRuleSettingsModel>(uri);
    }

    updateTrigger(trigger: ITriggerRuleSettingsModel) {
        if (!trigger.guid) return;
        return this.put<ITriggerRuleSettingsModel>(
            ApiRoutes.TriggerUpdate.replace('{guid}', trigger.guid),
            {
                body: trigger,
            }
        );
    }

    deleteTrigger(guid: string) {
        const uri = ApiRoutes.TriggerGet.replace('{guid}', guid);
        return this.delete(uri);
    }

    // FAVORITES
    deleteFavorite(id: string) {
        const url = ApiRoutes.FavoriteItem.replace('{id}', id);
        return this.delete<IResponse>(url);
    }

    getFavorites(typeId: number, def?: boolean) {
        const params = new URLSearchParams();
        params.set('typeId', String(typeId));

        if (def) {
            params.set('def', 'true');
        }

        const queryString = params.toString();
        const url = `${ApiRoutes.Favorites}?${queryString}`;

        return this.get<IFavoriteModel[]>(url);
    }

    addFavorite(model: IFavoriteModel) {
        const body: IFavoriteModel = model;
        return this.post<IResponseModel<string>>(ApiRoutes.Favorites, { body });
    }

    // EXPERTS
    getExperts() {
        return this.get<IExpertListModel[]>(ApiRoutes.GetExperts);
    }

    getExpertById(expertId: string) {
        return this.get<IExpertDetailModel>(ApiRoutes.GetExpert.replace('{id}', expertId));
    }

    isExpertNameExists(expertLastName: string, expertId: string) {
        return this.get<boolean>(
            ApiRoutes.ExpertNameExists.replace('{lastName}', expertLastName) + '?id=' + expertId
        );
    }

    createExpert(model: IExpertCreateModel) {
        return this.post<IExpertModel>(ApiRoutes.CreateExpert, { body: model });
    }

    updateExpert(model: IExpertCreateModel) {
        return this.put<IResponseModel<IExpertModel>>(ApiRoutes.UpdateExpert, { body: model });
    }

    deleteExpert(expertGuid: string) {
        return this.delete<IResponse>(ApiRoutes.DeleteExpert.replace('{id}', expertGuid));
    }

    // CASE EXPERTS
    getCaseExpertById(caseExpertId: string) {
        return this.get<ICaseExpertViewDetailModel>(
            ApiRoutes.GetCaseExpert.replace('{id}', caseExpertId)
        );
    }

    createCaseExpert(caseExpert: ICreateCaseExpertModel) {
        return this.post<ICaseExpertViewDetailModel>(ApiRoutes.CreateCaseExpert, {
            body: caseExpert,
        });
    }

    updateCaseExpert(caseExpert: IUpdateCaseExpertModel) {
        return this.put<ICaseExpertViewDetailModel>(ApiRoutes.UpdateCaseExpert, {
            body: caseExpert,
        });
    }

    deleteCaseExpert(caseExpertGuid: string) {
        return this.delete<IResponse>(ApiRoutes.DeleteCaseExpert.replace('{id}', caseExpertGuid));
    }

    // collaboration reports
    saveCollaborationReport(saveModel: IReportSaveModel) {
        const body: IReportSaveModel = saveModel;
        return this.post<IReportModel>(ApiRoutes.CollaborationReportSave, { body });
    }

    getCollaborationReport(reportGuid: string) {
        return this.get<IReportModel>(
            ApiRoutes.CollaborationReportGetComprehensive.replace(
                '{collaborationReportId}',
                reportGuid
            )
        );
    }

    getCollaborationReportHistory(reportGuid: string) {
        return this.get<IUserAuditModel[]>(
            ApiRoutes.CollaborationReportHistory.replace('{collaborationReportId}', reportGuid)
        );
    }

    deleteCollaborationReport(collaborationReportId: string) {
        return this.delete<IResponse>(
            ApiRoutes.CollaborationReportDelete.replace(
                '{collaborationReportId}',
                collaborationReportId
            )
        );
    }

    getCollabReportChildren(reportGuid: string, entityTypeId: number) {
        const url = ApiRoutes.ChildDocuments.replace('{id}', reportGuid).replace(
            '{entityTypeId}',
            entityTypeId.toString()
        );

        return this.get<IViewDocumentModel[]>(url);
    }

    getCollaborationReportStatus(reportGuid: string) {
        return this.get<ICollabReportStatusModel[]>(
            ApiRoutes.CollaborationReportGetStatus.replace('{collaborationReportId}', reportGuid)
        );
    }

    // REPORTS
    getReportTypes() {
        return this.get<IReportTypeModel[]>(ApiRoutes.Lookup + LookupDataEnums.ReportTypes);
    }

    getReportTypeDetail(id: number) {
        return this.get<IReportTypeModel>(
            ApiRoutes.ReportTypeDetail.replace('{id}', id.toString())
        );
    }

    getStaticDataDate(documentTypeId: number) {
        const url = ApiRoutes.StaticReportDataDate.replace(
            '{documentTypeId}',
            documentTypeId.toString()
        );
        return this.get<string>(url);
    }

    getSnapshotViewData(endDate: string, getLiveData: boolean) {
        return this.get<ISnapshotViewModel>(
            ApiRoutes.ReportDataByReportId.replace(
                '{id}',
                SystemGeneratedDocumentsTypesEnum.Snapshot.Value.toString()
            )
                .replace('{getLiveData}', getLiveData.toString())
                .replace('{endDate?}', endDate.toString())
        );
    }

    getTimelineReportData<M>(input: ITimelineReportInputModel) {
        const body: ITimelineReportInputModel = input;
        return this.post<M>(ApiRoutes.TimelineRecap, { body });
    }

    // USERS PROFILE
    getUserZoneProfiles(search: IUserZoneProfileSearchModel) {
        const body: IUserZoneProfileSearchModel = search;
        return this.post<IUserZoneProfileModel[]>(ApiRoutes.UserZoneProfiles, {
            body,
        });
    }

    // BOARD
    getBoardCaseDetails(
        type: string,
        parameters: IBoardParametersModel,
        id?: string,
        date?: string
    ) {
        const body: IBoardParametersModel = parameters;
        let url = ApiRoutes.BoardCaseDetails.replace('{type}', type);
        if (id) url += '&id=' + id;
        if (date) url += '&date=' + date;

        return this.post<IReportCaseDetailModel[]>(url, { body });
    }

    getBoardComments() {
        return this.get<IBoardComment[]>(ApiRoutes.BoardComments);
    }

    saveBoardComments(comments: IBoardComment[]) {
        const body: IBoardComment[] = comments;
        return this.post<IBoardComment[]>(ApiRoutes.BoardComments, { body });
    }

    // CASE DEPOSITION
    createDeposition(deposition: IDepositionModel) {
        const body: IDepositionModel = deposition;
        return this.post<IDepositionModel>(ApiRoutes.Deposition, { body });
    }

    updateDeposition(deposition: IDepositionModel) {
        const body: IDepositionModel = deposition;
        return this.put<IDepositionModel>(ApiRoutes.Deposition, { body });
    }

    deleteDeposition(guid: string) {
        return this.delete(ApiRoutes.Deposition + '/' + guid);
    }

    // ENTITY
    getEntityConversations(entityTypeId: number, entityGuid: string) {
        const url = ApiRoutes.EntityConversationGetAll.replace(
            '{entityTypeId}',
            entityTypeId.toString()
        ).replace('{entityGuid}', entityGuid.toString());

        return this.get<IEntityConversationModel[]>(url);
    }

    createEntityConversation(newConvo: IEntityConversationCreateModel) {
        return this.post<IEntityConversationModel>(ApiRoutes.EntityConversationCreate, {
            body: newConvo,
        });
    }

    obtainEntityLease(entityTypeId: number, entityGuid: string, durationInMs: number) {
        const url = ApiRoutes.EntityLeaseObtain.replace('{entityId}', entityGuid).replace(
            '{entityTypeId}',
            entityTypeId.toString()
        );
        return this.put<IObtainEntityLeaseResponse>(url, { body: { milliseconds: durationInMs } });
    }

    releaseEntityLease(entityLeaseGuid: string) {
        const url = ApiRoutes.EntityLeaseRelease.replace('{entityLeaseGuid}', entityLeaseGuid);

        return this.put<boolean>(url);
    }

    pingEntityLease(entityLeaseGuid: string) {
        const url = ApiRoutes.EntityLeasePing.replace('{entityLeaseGuid}', entityLeaseGuid);

        return this.put<IResponse>(url);
    }

    saveEntityAssertions(assertions: IEntityAssertionModel[]) {
        const body: IEntityAssertionModel[] = assertions;
        return this.put<IEntityAssertionModel[]>(ApiRoutes.EntityAssertions, {
            body,
        });
    }

    deleteEntityDocument(
        entityGuid: string,
        entityTypeId: number,
        documentGuid: string,
        noteTypeId?: number,
        strategyTypeId?: number
    ) {
        //// TODO: DJS change method signature to accept a single request object instead of multiple parameters and update all callers accordingly
        var requestBody: IDocumentDeleteRequestModel = {
            documentGuid,
            entityGuid,
            entityTypeId,
            noteTypeId,
            strategyTypeId,
        }
        return this.delete<IDocumentModel>(ApiRoutes.EntityDocumentDelete, {
            body: requestBody,
        });
    }

    // STRATEGIES
    getStrategicPlanEntityAssertions() {
        return this.get<IEntityAssertionModel[]>(ApiRoutes.StrategicPlan);
    }

    getExecutionPlanEntityAssertions() {
        return this.get<IEntityAssertionModel[]>(ApiRoutes.ExecutionPlan);
    }

    getEntityStrategies(type: number) {
        return this.get<IStrategySummaryModel[]>(ApiRoutes.Strategy + '?type=' + type.toString());
    }

    addStrategy(data: ICreateStrategyModel) {
        const body: ICreateStrategyModel = data;
        return this.post<IResponse>(ApiRoutes.Strategy, { body });
    }

    updateStrategyDetails(id: string, data: IUpdateStrategyModel) {
        const body: IUpdateStrategyModel = data;
        return this.put<IResponse>(ApiRoutes.StrategyDetail.replace('{id}', id), {
            body,
        });
    }

    removeStrategy(guid: string, strategyEntityTypeId: number) {
        return this.delete<IResponse>(
            ApiRoutes.StrategyDetail.replace('{id}', guid) + '?type=' + strategyEntityTypeId
        );
    }

    getPlaintiffsFirmsStrategyDetails(id: string) {
        return this.get<IPlaintiffsFirmStrategyDetailModel>(
            ApiRoutes.StrategyDetail.replace('{id}', id) +
                '?strategyDetailType=' +
                StrategyTypes.PlaintiffsFirm.Value
        );
    }

    getJurisdictionStrategyDetails(id: string) {
        return this.get<IJurisdictionStrategyDetailModel>(
            ApiRoutes.StrategyDetail.replace('{id}', id) +
                '?strategyDetailType=' +
                StrategyTypes.Jurisdiction.Value
        );
    }

    getLocalCounselStrategyDetails(id: string) {
        return this.get<ILocalCounselStrategyDetailModel>(
            ApiRoutes.StrategyDetail.replace('{id}', id) +
                '?strategyDetailType=' +
                StrategyTypes.LocalCounsel.Value
        );
    }

    saveCaseFileZoneDefenseCounsels(caseGuid: string, caseDefenseCounselGuids: string[]) {
        const body = caseDefenseCounselGuids;

        return this.put<IAssociatedOrganizationModel[]>(
            ApiRoutes.SaveCaseFileDefenseCounsels.replace('{id}', caseGuid),
            {
                body,
            }
        );
    }

    // PROJECTS
    getProjects() {
        return this.get<IProjectModel[]>(ApiRoutes.Projects);
    }

    addProject(data: IProjectModel) {
        const body: IProjectModel = data;
        return this.post<IResponse>(ApiRoutes.Projects, { body });
    }

    updateProject(id: string, data: IProjectModel) {
        const body: IProjectModel = data;
        return this.put<IEntityAssertionModel[]>(ApiRoutes.ProjectDetail.replace('{id}', id), {
            body,
        });
    }

    removeProject(guid: string) {
        return this.delete<IResponse>(ApiRoutes.ProjectDetail.replace('{id}', guid));
    }

    getProjectDetails(id: string) {
        return this.get<IProjectModel>(ApiRoutes.ProjectDetail.replace('{id}', id));
    }

    // DOCUMENTS
    deleteMyDocument(documentGuid: string) {
        let url = ApiRoutes.MyDocumentsDelete.replace('{id}', documentGuid);
        const queryStrings: string[] = [];
        if (queryStrings.length > 0) url += '?' + queryStrings.join('&');
        return this.delete<IDocumentModel>(url);
    }

    addExternalDocuments(documents: IDocumentRequestModel) {
        return this.post<IResponseModel<IDocumentModel[]>>(ApiRoutes.AddExternalDocuments, {
            body: documents,
        });
    }

    saveDocumentMetaData(request: ISaveDocumentMetaDataRequest) {
        console.log(request);
        return this.post<IResponseModel<IDocumentModel>>(ApiRoutes.EntityDocumentUpdateMetaData, {
            body: request,
        });
    }

    getMyDocuments(searchModel: IDocumentSearchModel, pageNumber: number, pageSize: number) {
        const url = ApiRoutes.Documents.replace('{pageNumber}', pageNumber.toString()).replace(
            '{pageSize}',
            pageSize.toString()
        );
        return this.post<IViewDocumentModel[]>(url, { body: searchModel });
    }

    getMyDocumentsCount(searchModel: IDocumentSearchModel) {
        const body: IDocumentSearchModel = searchModel;
        return this.post<IResponseModel<number>>(ApiRoutes.MyDocumentsCount, { body });
    }

    getCaseResolutionDraft(caseId: string) {
        return this.get<IDocumentModel[]>(
            ApiRoutes.CaseResolutionDraftGet.replace('{caseResolutionPlanId}', caseId)
        );
    }

    // TASKS / ASSIGNMENTS
    getMasterTasks(types?: string) {
        let url = ApiRoutes.MasterTasks;
        if (types) {
            url = url + '?types=' + types;
        }

        return this.get<ICheckmateTaskModel[]>(url);
    }

    getCaseTaskById(caseGuid: string, casePlanGuid: string, taskGuid: string) {
        return this.get<ICheckmateTaskModel>(
            ApiRoutes.CaseTaskDetails.replace('{id}', caseGuid)
                .replace('{casePlanId}', casePlanGuid)
                .replace('{taskId}', taskGuid)
        );
    }

    createTask(caseGuid: string, casePlanGuid: string, newTask: ICreateCheckmateTaskModel) {
        const body: ICreateCheckmateTaskModel = newTask;
        return this.post<ICreateCheckmateTaskModel[]>(
            ApiRoutes.CaseTaskNew.replace('{id}', caseGuid).replace('{casePlanId}', casePlanGuid),
            { body }
        );
    }

    updateTasks(caseGuid: string, casePlanGuid: string, tasks: ICheckmateTaskModel[]) {
        const body: ICheckmateTaskModel[] = tasks;
        const route = ApiRoutes.CasePlanTask.replace('{id}', caseGuid).replace(
            '{casePlanId}',
            casePlanGuid
        );
        return this.put<ICheckmateTaskModel[]>(route, { body });
    }

    deleteTasks(caseGuid: string, casePlanGuid: string, taskGuids: string[]) {
        const body: string[] = taskGuids;
        const route = ApiRoutes.CasePlanTask.replace('{id}', caseGuid).replace(
            '{casePlanId}',
            casePlanGuid
        );
        return this.delete(route, { body });
    }

    updateCaseTaskAssignments(
        caseGuid: string,
        casePlanGuid: string,
        assignments: IAssignmentModel[]
    ) {
        const body: IAssignmentModel[] = assignments;
        const route = ApiRoutes.CasePlanTaskAssignments.replace('{id}', caseGuid).replace(
            '{casePlanId}',
            casePlanGuid
        );
        return this.put<IAssignmentModel[]>(route, { body });
    }

    async getCaseAssessmentCaseStatistics(caseGuid: string) {
        const url = ApiRoutes.AssessmentCaseStatistics.replace('{caseId}', caseGuid);
        return await this.get<ILikeCaseStatistics>(url);
    }

    async getCaseAssessmentBestFitCases(caseGuid: string) {
        const url = ApiRoutes.AssessmentBestFitCases.replace('{caseId}', caseGuid);
        return await this.get<IBestFitCase[]>(url);
    }

    async getCaseAssessmentThreatScore(caseGuid: string) {
        const url = ApiRoutes.AssessmentThreatScore.replace('{caseId}', caseGuid);
        return await this.get<IThreatScore>(url);
    }

    async getCaseAssessmentPredictedOutcome(caseGuid: string) {
        const url = ApiRoutes.AssessmentPredictedOutcome.replace('{caseId}', caseGuid);
        return await this.get<IPredictedOutcomeOutput>(url);
    }

    async getCaseAssessmentHasModel() {
        const url = ApiRoutes.AssessmentHasModel;
        return await this.get<boolean>(url);
    }
}

export default CheckmateZoneApiClient;
